import store from '@/store'
import { getToken, getUserInfo } from './storage'

// 路由路径 → menu_key 映射（必须与后端 permission_constants.py 中 MENU_SCHEMA[].children[].route 保持完全一致）
export const ROUTE_TO_MENU = {
  '/admin/dashboard': 'dashboard',
  '/admin/employees': 'employee_profile',
  '/admin/salary-manage': 'employee_salary',
  '/admin/salary-dashboard': 'salary_dashboard',
  '/admin/salary-upload': 'salary_upload',
  '/admin/upload-history': 'salary_history',
  '/admin/salary-send': 'salary_view',
  '/admin/salary-records': 'salary_record',
  '/admin/clients': 'project_client',
  '/admin/project-dashboard': 'project_dashboard',
  '/admin/projects': 'project_manage',
  '/admin/project-payments': 'project_payment',
  '/admin/project-settlements': 'project_settlement',
  '/admin/partner-settlements': 'partner_settlement',
  '/admin/finance/dashboard': 'finance_dashboard',
  '/admin/finance/income-vouchers': 'finance_income',
  '/admin/finance/expense-vouchers': 'finance_expense',
  '/admin/finance/transfer-vouchers': 'finance_transfer',
  '/admin/finance/fund-accounts': 'finance_account',
  '/admin/finance/categories': 'finance_category',
  '/admin/finance/partners': 'finance_partner',
  '/admin/company/overview': 'company_overview',
  '/admin/company/qualifications': 'company_qualification',
  '/admin/company/licenses': 'company_license',
  '/admin/company/iprs': 'company_ipr',
  '/admin/company/accounts': 'company_account',
  '/admin/company/contracts': 'company_contract',
  '/admin/company/project-contracts': 'company_project_contract',
  '/admin/company/general': 'company_general',
  '/admin/approval/types': 'approval_type',
  '/admin/approval/flows': 'approval_flow',
  '/admin/approval/center': 'approval_center',
  '/admin/approval/comp': 'approval_comp',
  '/admin/approval/leave': 'leave_quota',
  '/admin/appraisal/settings': 'appraisal_settings',
  '/admin/appraisal/records': 'appraisal_manage',
  '/admin/appraisal/stats': 'appraisal_stats',
  '/admin/system-settings/announcements': 'announcement',
  '/admin/system-settings/feedback': 'system_feedback',
  '/admin/system-settings/roles': 'role',
  '/admin/system-settings/organization': 'system_organization',
  '/admin/system-settings/employee-menus': 'system_employee_menu',
  '/admin/system-settings/holiday': 'system_holiday',
  '/admin/system-settings/email': 'system_email',
  '/admin/system-settings/work-time': 'system_settings',
  '/admin/system-settings/column-config': 'system_column',
  '/admin/system-settings/login-logs': 'system_login_log',
  '/admin/system-settings/operation-logs': 'system_operation_log'
}

/**
 * 根据路由路径解析对应的 menu_key
 * 优先使用 to.meta.menuKey，其次查 ROUTE_TO_MENU 表
 */
export function getRouteMenuKey(route) {
  if (!route) return null
  const meta = route.meta || {}
  if (meta.menuKey) return meta.menuKey
  const path = route.path || ''
  if (ROUTE_TO_MENU[path]) return ROUTE_TO_MENU[path]
  // 二级路由：父级的 path + 子级的 path 拼合后再查
  const matched = route.matched || []
  for (let i = matched.length - 1; i >= 0; i--) {
    const r = matched[i]
    if (r && r.path && ROUTE_TO_MENU[r.path]) return ROUTE_TO_MENU[r.path]
  }
  return null
}

/**
 * 路由级访问判定：
 * - admin: 始终 true（但角色管理本身 menu 限制由前端 meta.role=admin 控制）
 * - sub_admin: 需要对应 menuKey 的 read=true
 * - employee: 无 admin 访问权
 */
export function canAccessRoute(route) {
  if (isAdmin()) return true
  if (isEmployee()) return false
  const menuKey = getRouteMenuKey(route)
  if (!menuKey) {
    // 找不到对应菜单（比如角色管理页 /admin/system-settings/roles 不在 ROUTE_TO_MENU 中）：由 meta.role=admin 兜底判定，这里返回 false 让上层逻辑走 dashboard
    return false
  }
  return hasPerm(menuKey, 'read')
}

export function hasToken() {
  return !!getToken()
}

export function getUserRole() {
  const userInfo = getUserInfo()
  if (userInfo && userInfo.role) {
    return userInfo.role
  }
  return null
}

export function isAdmin() {
  return getUserRole() === 'admin'
}

export function isSubAdmin() {
  return getUserRole() === 'sub_admin'
}

export function isEmployee() {
  return getUserRole() === 'employee'
}

export function checkRole(requiredRole) {
  const role = getUserRole()
  if (!role) return false
  if (Array.isArray(requiredRole)) {
    return requiredRole.includes(role)
  }
  return role === requiredRole
}

/**
 * 检查当前用户是否有某个菜单动作权限
 * @param {string} menuKey - 菜单key，如 'employee_profile'
 * @param {string} action - 动作key，如 'read'/'create'/'edit'/'delete'/'export'/'import'/'send' ...
 * @returns {boolean}
 */
function _toCamel(str) {
  if (!str || typeof str !== 'string') return str
  return str.replace(/_([a-z0-9])/g, (_, c) => c.toUpperCase())
}

export function hasPerm(menuKey, action = 'read') {
  // 超级管理员全放行
  if (isAdmin()) return true
  // 员工端无管理权限
  if (isEmployee()) return false
  // 副管理员：从 store.user.menuPermissions 中找
  const menuPermissions = store && store.state && store.state.user && store.state.user.menuPermissions
  if (!menuPermissions) return false
  // 优先按 snake_case 查（loadPermissions 已规范化转回 snake）
  const snakePerm = menuPermissions[menuKey] || {}
  if (snakePerm[action] === true) return true
  // fallback：兼容 camelCase（旧缓存或拦截器漏转）
  const camelKey = _toCamel(menuKey)
  if (camelKey !== menuKey) {
    const camelPerm = menuPermissions[camelKey] || {}
    if (camelPerm[action] === true) return true
  }
  return false
}

/**
 * 批量检查菜单权限（简化模板中多次调用）
 * 例如：v-if="canAny(['employee_profile:create', 'employee_profile:edit'])"
 */
export function canAny(permExprs) {
  const arr = Array.isArray(permExprs) ? permExprs : [permExprs]
  return arr.some(expr => {
    const [menuKey, action] = String(expr).split(':')
    if (!menuKey) return false
    return hasPerm(menuKey, action || 'read')
  })
}
